// Curated city × material combinations for SEO landing pages.
// Each entry powers a /gravel-delivery/:citySlug/:materialSlug route via
// pages/CityMaterial.tsx. The list mixes King and Pierce County cities so
// the index page shows our full service footprint at a glance, not just
// the southern corridor.
import { categories } from './products'
import { getZoneForPosition } from './deliveryZones'
export type ServiceCity = {
  slug: string
  name: string
  state: string
  county: 'King' | 'Pierce'
  zone: number
  // Approximate city centroid [lat, lng] — used by the drift-detection
  // check below to make sure the curated `zone` value matches what the
  // delivery-zone polygons would actually return for this city. If the
  // polygons get redrawn, the check logs a warning and we can update the
  // `zone` value here in one place.
  coords: [number, number]
  // Display address used to prefill the estimator and seed the
  // Nominatim geocode on the destination page.
  geocodeHint: string
  neighborhoods: string[]
  // Common short trip notes — used in the page intro paragraph.
  blurb: string
}
export const SERVICE_CITIES: ServiceCity[] = [
  {
    slug: 'tacoma',
    name: 'Tacoma',
    state: 'WA',
    county: 'Pierce',
    zone: 3,
    coords: [47.2529, -122.4443],
    geocodeHint: 'Tacoma, WA',
    neighborhoods: [
      'North End',
      'Stadium District',
      'Hilltop',
      'South Tacoma',
      'Browns Point',
      'University Place',
    ],
    blurb:
      'We deliver into Tacoma daily — most loads route directly down I-5 with reliable next-day scheduling.',
  },
  {
    slug: 'seattle',
    name: 'Seattle',
    state: 'WA',
    county: 'King',
    zone: 3,
    coords: [47.6062, -122.3321],
    geocodeHint: 'Seattle, WA',
    neighborhoods: [
      'West Seattle',
      'Beacon Hill',
      'Rainier Valley',
      'Georgetown',
      'Magnolia',
      'Queen Anne',
    ],
    blurb:
      'We deliver into Seattle daily. South-end neighborhoods route fastest; north of the ship canal adds an hour to dispatch.',
  },
  {
    slug: 'puyallup',
    name: 'Puyallup',
    state: 'WA',
    county: 'Pierce',
    zone: 4,
    coords: [47.1854, -122.2929],
    geocodeHint: 'Puyallup, WA',
    neighborhoods: [
      'South Hill',
      'Riverside',
      'North Puyallup',
      'Edgerton',
      'Glacier View',
    ],
    blurb:
      'Puyallup sits at our outer Pierce reach — book a day ahead so we can line up the right truck and timing.',
  },
  {
    slug: 'bellevue',
    name: 'Bellevue',
    state: 'WA',
    county: 'King',
    zone: 3,
    coords: [47.6101, -122.2015],
    geocodeHint: 'Bellevue, WA',
    neighborhoods: [
      'Downtown',
      'Bridle Trails',
      'Somerset',
      'Newport',
      'Lake Hills',
      'Wilburton',
    ],
    blurb:
      'Bellevue routes off I-405 — solid lead times for both Eastside residential and commercial sites.',
  },
  {
    slug: 'kent',
    name: 'Kent',
    state: 'WA',
    county: 'King',
    zone: 1,
    coords: [47.3809, -122.2348],
    geocodeHint: 'Kent, WA',
    neighborhoods: [
      'East Hill',
      'West Hill',
      'Panther Lake',
      'Meridian',
      'Lake Meridian',
    ],
    blurb:
      'Kent is in our innermost delivery zone — fastest routes and consistent same-day availability.',
  },
  {
    slug: 'renton',
    name: 'Renton',
    state: 'WA',
    county: 'King',
    zone: 1,
    coords: [47.4829, -122.2171],
    geocodeHint: 'Renton, WA',
    neighborhoods: [
      'Highlands',
      'Kennydale',
      'Talbot Hill',
      'The Landing',
      'Fairwood',
    ],
    blurb:
      'Renton sits in our innermost delivery zone — most loads roll within an hour of confirmation during business hours.',
  },
  {
    slug: 'auburn',
    name: 'Auburn',
    state: 'WA',
    county: 'King',
    zone: 2,
    coords: [47.3073, -122.2285],
    geocodeHint: 'Auburn, WA',
    neighborhoods: [
      'Lea Hill',
      'West Hill',
      'Lakeland Hills',
      'Downtown Auburn',
    ],
    blurb:
      'Auburn deliveries route directly off 167 — quick turnarounds for both residential and contractor jobsites.',
  },
  {
    slug: 'federal-way',
    name: 'Federal Way',
    state: 'WA',
    county: 'King',
    zone: 2,
    coords: [47.3223, -122.3126],
    geocodeHint: 'Federal Way, WA',
    neighborhoods: [
      'Twin Lakes',
      'Mirror Lake',
      'Lakota',
      'Redondo',
      'Brigadoon',
    ],
    blurb:
      'Federal Way sits in our core delivery zone with reliable same-week scheduling.',
  },
  {
    slug: 'redmond',
    name: 'Redmond',
    state: 'WA',
    county: 'King',
    zone: 3,
    coords: [47.674, -122.1215],
    geocodeHint: 'Redmond, WA',
    neighborhoods: [
      'Education Hill',
      'Grass Lawn',
      'Idylwood',
      'Overlake',
      'Bear Creek',
    ],
    blurb:
      'Redmond deliveries pair well with weekday morning slots — book ahead during peak landscape season.',
  },
  {
    slug: 'sammamish',
    name: 'Sammamish',
    state: 'WA',
    county: 'King',
    zone: 3,
    coords: [47.6163, -122.0356],
    geocodeHint: 'Sammamish, WA',
    neighborhoods: [
      'Klahanie',
      'Pine Lake',
      'Trossachs',
      'Beaver Lake',
      'Inglewood',
    ],
    blurb:
      'Sammamish sits at our outer Eastside reach — schedule a day or two ahead for best timing.',
  },
  {
    slug: 'gig-harbor',
    name: 'Gig Harbor',
    state: 'WA',
    county: 'Pierce',
    zone: 4,
    coords: [47.3293, -122.5826],
    geocodeHint: 'Gig Harbor, WA',
    neighborhoods: [
      'Downtown',
      'Canterwood',
      'Rosedale',
      'Crescent Valley',
      'Fox Island',
      'Artondale',
    ],
    blurb:
      'Gig Harbor deliveries route over the Narrows Bridge — book a day ahead and we’ll line up the timing around bridge traffic.',
  },
  {
    slug: 'black-diamond',
    name: 'Black Diamond',
    state: 'WA',
    county: 'King',
    zone: 1,
    coords: [47.3087, -122.0046],
    geocodeHint: 'Black Diamond, WA',
    neighborhoods: [
      'Ten Trails',
      'Lake Sawyer',
      'Pipe Lake',
      'Diamond Lake',
      'Old Town',
    ],
    blurb:
      'Black Diamond sits in our inner delivery zone with quick turnarounds — popular for rural driveway builds and acreage projects.',
  },
  {
    slug: 'bonney-lake',
    name: 'Bonney Lake',
    state: 'WA',
    county: 'Pierce',
    zone: 3,
    coords: [47.1865, -122.1865],
    geocodeHint: 'Bonney Lake, WA',
    neighborhoods: [
      'Tehaleh',
      'Sky Island',
      'Lake Tapps',
      'Falling Water',
      'Eastown',
    ],
    blurb:
      'Bonney Lake routes off the Puyallup corridor — schedule a day ahead so we can route the truck efficiently.',
  },
]
// Featured products to generate combo pages for. Only top sellers — long-tail
// products still get organic SEO via their /shop/:slug product pages.
export const SERVICE_PRODUCT_SLUGS = [
  '5-8-minus', // 5/8" Minus
  '3-way-topsoil',
  '7-8-drain-rock', // 7/8" Drain Rock
  'medium-bark',
]
export function getServiceProduct(slug: string) {
  for (const cat of categories) {
    const p = cat.products.find((pp) => pp.slug === slug)
    if (p)
      return {
        product: p,
        category: cat,
      }
  }
  return null
}
export function getServiceCity(slug: string): ServiceCity | undefined {
  return SERVICE_CITIES.find((c) => c.slug === slug)
}

// ---------- Dev-only zone-consistency check ----------
//
// Each ServiceCity carries a curated `zone` value used by the city
// landing pages and SEO copy. The cart's delivery-fee math, however,
// uses the live polygons in `deliveryZones.ts` via getZoneForPosition.
// If the polygons get redrawn and someone forgets to update the curated
// list (or vice versa), the city page and the cart can disagree about
// the fee — bad UX and a trust hit.
//
// This check runs once at module-load in development and warns in the
// browser console for any city whose curated `zone` doesn't match what
// the polygons say. It's a no-op in production builds, and it never
// auto-corrects — the operator decides which source is authoritative
// and updates the other to match.
if (typeof process !== 'undefined' && process.env.NODE_ENV !== 'production') {
  const mismatches: { slug: string; curated: number; polygon: number }[] = []
  for (const city of SERVICE_CITIES) {
    const result = getZoneForPosition(city.coords[0], city.coords[1])
    if (result.zone !== city.zone) {
      mismatches.push({
        slug: city.slug,
        curated: city.zone,
        polygon: result.zone,
      })
    }
  }
  if (mismatches.length > 0 && typeof console !== 'undefined') {
    // eslint-disable-next-line no-console
    console.warn(
      '[SERVICE_CITIES] Zone drift detected — city pages and the cart will quote different delivery fees:\n' +
        mismatches
          .map(
            (m) =>
              `  • ${m.slug}: curated zone ${m.curated} vs polygon zone ${m.polygon || 'outside'}`,
          )
          .join('\n') +
        '\nUpdate either data/cityMaterialContent.ts (city zone) or data/deliveryZones.ts (polygon) so they agree.',
    )
  }
}
